import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import "../style.scss";
import { formatDate } from "../../../../../../formatDate";
import { Link, useMatch } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppContext } from "../../../../../../../AppContext";

function ListBrand(props) {
  const { user, chimeraserv, components } = useAppContext();
  const contextProps = { user, chimeraserv, components };
  const [table, setTable] = useState({
    head: ["Status", "Last Update", "Group", "Brand", "Outlet", "Actions"],
    data: false,
  });
  const [load, setLoad] = useState(0); // 0: loading, 1: data loaded, 2: ready for export
  const [outletStatus, setOutletStatus] = useState({}); // Real-time status for each outlet
  const [searchTerm, setSearchTerm] = useState(""); // Search filter for outlets
  const [filterStatus, setFilterStatus] = useState("all"); // Filter by online/offline
  const [filterGroup, setFilterGroup] = useState("all"); // Filter by group
  const [filterBrand, setFilterBrand] = useState("all"); // Filter by brand
  const [sortBy, setSortBy] = useState("status"); // Sort field
  const [sortOrder, setSortOrder] = useState("desc"); // Sort order (asc/desc)

  // Ref to track previous export state
  const prevExport = useRef(props?.export || false);
  const statusFetchedRef = useRef(false); // Track if status has been fetched for current data

  // Get the base path for links
  const match = useMatch("/app/dashboard/*");
  const basePath = "/app";

  // Move sort function BEFORE useMemo
  const sort = (a, b) => {
    if (parseInt(a.info.status) < parseInt(b.info.status)) {
      return -1;
    }
    if (parseInt(a.info.status) > parseInt(b.info.status)) {
      return 1;
    }
    return 0;
  };

  // Memoize the flattened data for performance
  const flattenedData = useMemo(() => {
    if (!table.data || !Array.isArray(table.data)) return [];

    return [].concat
      .apply(
        [],
        [].concat.apply(
          [],
          table.data.map((data) =>
            data.brands.map((brand) =>
              brand.chains
                .filter((chain) => chain.status == 1)
                .filter((chain) => chain.outletExp < Date.now())
                .map((chain) => ({
                  group: { _id: data._id, name: data.name },
                  brand: { name: brand.name },
                  ...chain,
                }))
            )
          )
        )
      )
      .sort(sort);
  }, [table.data]);

  // Filter outlets based on search term and filters
  const filteredData = useMemo(() => {
    if (
      !searchTerm.trim() &&
      filterStatus === "all" &&
      filterGroup === "all" &&
      filterBrand === "all"
    )
      return flattenedData;

    const term = searchTerm.toLowerCase();
    return flattenedData.filter((chain) => {
      // Search filter
      const matchesSearch =
        chain.name.toLowerCase().includes(term) || // Outlet name
        chain.chaKey.toLowerCase().includes(term) || // Chain key
        chain.brand.name.toLowerCase().includes(term) || // Brand name
        chain.group.name.toLowerCase().includes(term); // Group name

      // Status filter
      const isOnline =
        outletStatus[chain.chaKey] !== undefined
          ? outletStatus[chain.chaKey]
          : chain.info.status;
      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "online" && isOnline === 1) ||
        (filterStatus === "offline" && isOnline === 0);

      // Group filter
      const matchesGroup =
        filterGroup === "all" || chain.group._id === filterGroup;

      // Brand filter
      const matchesBrand =
        filterBrand === "all" || chain.brand.braKey === filterBrand;

      return matchesSearch && matchesStatus && matchesGroup && matchesBrand;
    });
  }, [
    flattenedData,
    searchTerm,
    filterStatus,
    filterGroup,
    filterBrand,
    outletStatus,
  ]);

  // Get unique groups for filter dropdown
  const availableGroups = useMemo(() => {
    if (!flattenedData.length) return [];
    return [
      ...new Map(
        flattenedData.map((item) => [item.group._id, item.group])
      ).values(),
    ];
  }, [flattenedData]);

  // Get unique brands for filter dropdown
  const availableBrands = useMemo(() => {
    if (!flattenedData.length) return [];
    const brandsMap = new Map();
    flattenedData.forEach((item) => {
      if (!brandsMap.has(item.brand.braKey)) {
        brandsMap.set(item.brand.braKey, item.brand);
      }
    });
    return Array.from(brandsMap.values());
  }, [flattenedData]);

  // Filter brands based on selected group
  const filteredBrands = useMemo(() => {
    if (filterGroup === "all") return availableBrands;
    return availableBrands.filter((brand) => {
      return flattenedData.some(
        (item) =>
          item.brand.braKey === brand.braKey && item.group._id === filterGroup
      );
    });
  }, [availableBrands, filterGroup, flattenedData]);

  useEffect(() => {
    const fetchData = async () => {
      if (contextProps.user && contextProps.user.data) {
        setLoad(0); // Start loading
        try {
          const response = await axios.get(
            contextProps.chimeraserv.urlGet + "/group",
            {
              headers: {
                Authorization: contextProps.user.data.usrKey,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
            }
          );
          let data = [];
          if (response.data && Object.keys(response.data).length) {
            data = response.data;
          }
          setTable((prev) => ({
            ...prev,
            data: data,
          }));
          setLoad(1); // Data loaded
        } catch (error) {
          setTable((prev) => ({
            ...prev,
            data: [],
          }));
          setLoad(1); // Even on error, mark as loaded
        }
      }
    };
    fetchData();
    fetchOutletStatus();
    // eslint-disable-next-line
  }, [contextProps.user, contextProps.chimeraserv.urlGet]);

  // Set load to 2 when data is ready
  useEffect(() => {
    if (load === 1 && table.data !== false) {
      setLoad(2);
    }
  }, [load, table.data]);

  // Fetch real-time status for all outlets (batched to prevent resource exhaustion)
  const fetchOutletStatus = useCallback(async () => {
    if (!flattenedData || flattenedData.length === 0) return;

    try {
      const headers = {
        Authorization: contextProps.user.data.usrKey,
        "Content-Type": "application/json",
        Accept: "application/json",
      };

      const batchSize = 2; // Fetch 2 outlets at a time
      const statusMap = {};

      // Process outlets in batches to avoid resource exhaustion
      for (let i = 0; i < flattenedData.length; i += batchSize) {
        const batch = flattenedData.slice(i, i + batchSize);

        const statusPromises = batch.map((chain) =>
          axios
            .get(
              `${contextProps.chimeraserv.urlGet}/status/connection/${chain.chaKey}`,
              {
                headers,
                timeout: 5000,
              }
            )
            .then((response) => {
              // Handle 204 No Content - treat as online
              const status =
                response.status === 204
                  ? 1
                  : response.data?.status || chain.info.status;
              return { chaKey: chain.chaKey, status };
            })
            .catch(() => ({
              chaKey: chain.chaKey,
              status: chain.info.status, // fallback to original status
            }))
        );

        const batchResults = await Promise.all(statusPromises);
        batchResults.forEach((result) => {
          statusMap[result.chaKey] = result.status;
        });

        // Add delay between batches to prevent overwhelming the server
        if (i + batchSize < flattenedData.length) {
          await new Promise((resolve) => setTimeout(resolve, 800));
        }
      }

      setOutletStatus(statusMap);
    } catch (error) {
      console.error("Error fetching outlet status:", error);
    }
  });

  // // Fetch outlet status once when data loads - only once per data refresh
  // useEffect(() => {
  //   if (load === 2 && flattenedData.length > 0 && !statusFetchedRef.current) {
  //     statusFetchedRef.current = true;
  //     fetchOutletStatus();
  //   }
  // }, [load]); // Removed fetchOutletStatus from dependencies to prevent loop

  // Reset fetch flag when data is being refreshed
  // useEffect(() => {
  //   if (load === 0) {
  //     statusFetchedRef.current = false;
  //   }
  // }, [load]);

  // Export logic
  useEffect(() => {
    if (prevExport.current !== props?.export && load === 2 && props?.getData) {
      prevExport.current = props.export;

      // Use filtered data if search is active, otherwise use all data
      const dataToExport = searchTerm ? filteredData : flattenedData;

      // Prepare export data based on flattened data (excluding Actions column)
      const exportData = dataToExport.map((chain, index) => {
        const isOnline =
          outletStatus[chain.chaKey] !== undefined
            ? outletStatus[chain.chaKey]
            : chain.info.status;
        return [
          isOnline ? "Online" : "Offline",
          formatDate.long(chain.info.lastUpdate),
          chain.group.name,
          chain.brand.name,
          chain.name,
        ];
      });

      // Get summary statistics
      const totalOutlets = dataToExport.length;
      const onlineOutlets = dataToExport.filter((chain) => {
        const isOnline =
          outletStatus[chain.chaKey] !== undefined
            ? outletStatus[chain.chaKey]
            : chain.info.status;
        return isOnline;
      }).length;
      const offlineOutlets = totalOutlets - onlineOutlets;

      // Group statistics
      const uniqueGroups = [
        ...new Set(dataToExport.map((chain) => chain.group.name)),
      ];
      const uniqueBrands = [
        ...new Set(dataToExport.map((chain) => chain.brand.name)),
      ];

      const summaryData = [
        ["Total Outlets", totalOutlets.toString()],
        ["Online Outlets", onlineOutlets.toString()],
        ["Offline Outlets", offlineOutlets.toString()],
        ["Total Groups", uniqueGroups.length.toString()],
        ["Total Brands", uniqueBrands.length.toString()],
        [
          "Online Percentage",
          totalOutlets > 0
            ? `${((onlineOutlets / totalOutlets) * 100).toFixed(1)}%`
            : "0%",
        ],
      ];

      props.getData({
        type: "table",
        title: "Outlet Management Summary",

        body: [
          // Main outlet data table (without Actions column)
          {
            head: [["Status", "Last Update", "Group", "Brand", "Outlet"]],
            body: exportData,
            sheetName: "Outlet List",
          },
          // Summary table
          {
            head: [["Summary", "Value"]],
            body: summaryData,
            sheetName: "Outlet Summary",
          },
        ],
        id: "exportPdf_OutletList",
      });
    }
    // eslint-disable-next-line
  }, [
    props?.export,
    load,
    filteredData,
    flattenedData,
    props?.getData,
    outletStatus,
    searchTerm,
    filterStatus,
    filterGroup,
    filterBrand,
  ]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setFilterStatus(e.target.value);
  };

  const handleGroupFilter = (e) => {
    setFilterGroup(e.target.value);
    setFilterBrand("all"); // Reset brand filter when group changes
  };

  const handleBrandFilter = (e) => {
    setFilterBrand(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterGroup("all");
    setFilterBrand("all");
  };

  const valueChange = async (group, brand, chain) => {
    let groupIndex = group.brands
      .map((brands, index) => ({
        ...brands,
        chains: brands.chains
          .map((chains, key) => ({ ...chains, index: key }))
          .find((chains) => chains.chaKey === chain.chaKey),
        index: index,
      }))
      .find((brands) => brands.braKey === brand.braKey);

    group.brands[groupIndex.index].chains[groupIndex.chains.index].status =
      group.brands[groupIndex.index].chains[groupIndex.chains.index].status ===
      1
        ? 0
        : 1;

    let data = {
      ...group,
    };

    try {
      await axios.put(
        contextProps.chimeraserv.urlPost + "/group/update",
        data,
        {
          headers: {
            Authorization: contextProps.user.data.usrKey,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      // Refresh data after successful update
      setLoad(0);
    } catch (error) {
      console.error("Error updating outlet status:", error);
    }
  };

  const renderData = () => {
    return filteredData.map((chain, key) => {
      // Use real-time status from outletStatus, fallback to chain.info.status
      const isOnline =
        outletStatus[chain.chaKey] !== undefined
          ? outletStatus[chain.chaKey]
          : chain.info.status;

      return (
        <tr key={key}>
          <td>
            <div className="name">
              <div
                className={["status", isOnline ? "" : "offline"].join(" ")}
              />
              {isOnline ? "Online" : "Offline"}
            </div>
          </td>
          <td>{formatDate.long(chain.info.lastUpdate)}</td>
          <td>{chain.group.name}</td>
          <td>{chain.brand.name}</td>
          <td>{chain.name}</td>
          <td className="actions">
            <Link to={`${basePath}/group/outlet/view/${chain.chaKey}`}>
              <FontAwesomeIcon className="icon" icon={["fas", "eye"]} /> View
            </Link>
          </td>
        </tr>
      );
    });
  };

  const Loader = contextProps.components.setting[1].component;

  return (
    <div id="exportPdf_OutletList">
      <div>
        {/* Search and Filter Controls */}
        <div className="table-controls" style={{ marginBottom: "15px" }}>
          <div className="search-section" style={{ marginBottom: "10px" }}>
            <div className="search-input-group">
              <FontAwesomeIcon
                className="search-icon"
                icon={["fas", "search"]}
              />
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search by outlet name, chaKey, brand, or group..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="clear-search"
                  onClick={() => setSearchTerm("")}
                  title="Clear search"
                >
                  <FontAwesomeIcon icon={["fas", "times"]} />
                </button>
              )}
            </div>
          </div>

          <div
            className="filters-section"
            style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
          >
            <select
              className="form-control filter-select"
              value={filterStatus}
              onChange={handleStatusFilter}
              style={{
                padding: "8px 12px",
                fontSize: "14px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
            </select>

            <select
              className="form-control filter-select"
              value={filterGroup}
              onChange={handleGroupFilter}
              style={{
                padding: "8px 12px",
                fontSize: "14px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              <option value="all">All Groups</option>
              {availableGroups.map((group) => (
                <option key={group._id} value={group._id}>
                  {group.name}
                </option>
              ))}
            </select>

            {/* <select
              className="form-control filter-select"
              value={filterBrand}
              onChange={handleBrandFilter}
              style={{
                padding: "8px 12px",
                fontSize: "14px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              <option value="all">All Brands</option>
              {filteredBrands.map((brand) => (
                <option key={brand.braKey} value={brand.braKey}>
                  {brand.name}
                </option>
              ))}
            </select>*/}

            <button
              type="button"
              className="btn btn-secondary clear-filters"
              onClick={clearFilters}
              title="Clear all filters"
              style={{
                padding: "8px 12px",
                backgroundColor: "#e58d35",
                border: "1px solid #ddd",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              <FontAwesomeIcon icon={["fas", "refresh"]} /> Clear
            </button>
          </div>
        </div>

        {/* Results Summary */}
        {table.data && (
          <div
            className="results-summary"
            style={{
              marginBottom: "10px",
              fontSize: "12px",
              color: "#666",
              display: "flex",
              gap: "10px",
              alignItems: "center",
            }}
          >
            Showing {filteredData.length} of {flattenedData.length} outlets
            {(searchTerm ||
              filterStatus !== "all" ||
              filterGroup !== "all" ||
              filterBrand !== "all") && (
              <span className="filtered-indicator" style={{ color: "#f0ad4e" }}>
                <FontAwesomeIcon icon={["fas", "filter"]} /> Filtered
              </span>
            )}
          </div>
        )}

        {!table.data ? (
          <Loader />
        ) : (
          <table className="table table-striped defaultTable">
            <thead>
              <tr>
                {table.head.map((head, key) => (
                  <th scope="col" key={key}>
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                renderData()
              ) : (
                <tr>
                  <td
                    colSpan={table.head.length}
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    <div>
                      <FontAwesomeIcon
                        icon={["fas", "search"]}
                        style={{
                          fontSize: "24px",
                          color: "#999",
                          marginBottom: "10px",
                          display: "block",
                        }}
                      />
                      <p style={{ color: "#666" }}>
                        No outlets found matching your search criteria
                      </p>
                      {(searchTerm ||
                        filterStatus !== "all" ||
                        filterGroup !== "all" ||
                        filterBrand !== "all") && (
                        <button className="btn btn-link" onClick={clearFilters}>
                          Clear filters to see all outlets
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ListBrand;
